import { Link } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  ArrowLeft, 
  FileText, 
  Sparkles, 
  User, 
  Ban, 
  FileEdit, 
  AlertTriangle,
  Scale,
  Power,
  RefreshCw,
} from 'lucide-react'

export function Terms() {
  useDocumentHead({
    title: 'Terms of Service',
    description: 'Terms of service for Badminton Leagues. Understand the rules and conditions for using our badminton league ranking service.',
  })

  const sections = [
    {
      icon: Sparkles,
      title: 'The Service',
      content: (
        <p className="text-muted-foreground">
          Badminton Leagues lets you track league competitions, record matches, 
          and manage groups. The service is free but may change in the future.
        </p>
      ),
    },
    {
      icon: User,
      title: 'Your Account',
      content: (
        <p className="text-muted-foreground">
          You're responsible for your account and keeping your login secure. 
          You must be at least 16 years old to use this service.
        </p>
      ),
    },
    {
      icon: Ban,
      title: 'Acceptable Use',
      content: (
        <ul className="space-y-1 text-muted-foreground text-sm">
          <li>• Don't break any laws</li>
          <li>• Don't harass other users</li>
          <li>• Don't try to hack or disrupt the service</li>
          <li>• Don't misuse the service</li>
        </ul>
      ),
    },
    {
      icon: FileEdit,
      title: 'Your Content',
      content: (
        <p className="text-muted-foreground">
          You own what you create. By posting, you let us display it as part of the service. 
          We can remove content at our discretion.
        </p>
      ),
    },
    {
      icon: AlertTriangle,
      title: 'No Guarantees',
      content: (
        <p className="text-muted-foreground">
          This is a hobby project provided <strong className="text-foreground">"as is"</strong>. 
          We make no promises about availability, features, or fitness for any purpose.
        </p>
      ),
    },
    {
      icon: Scale,
      title: 'Liability',
      content: (
        <p className="text-muted-foreground">
          We're not liable for any damages from your use of the service. 
          <strong className="text-foreground"> Use entirely at your own risk.</strong>
        </p>
      ),
    },
    {
      icon: Power,
      title: 'Termination',
      content: (
        <p className="text-muted-foreground">
          You can stop using the service anytime. We can suspend or terminate access 
          at any time, for any reason.
        </p>
      ),
    },
    {
      icon: RefreshCw,
      title: 'Changes',
      content: (
        <p className="text-muted-foreground">
          We can change these terms or the service at any time. Continued use means 
          you accept any changes.
        </p>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl flex-1">
        <Button variant="ghost" size="sm" asChild className="mb-6">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </Button>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 mb-4">
            <FileText className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
          <p className="text-muted-foreground">
            Last updated: December 2024
          </p>
        </div>

        {/* Intro */}
        <Card className="mb-8 border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <p className="text-center">
              Badminton Leagues is a hobby project by an individual, not a company. 
              By using this service, you agree to these terms.
            </p>
          </CardContent>
        </Card>

        {/* Sections Grid */}
        <div className="grid gap-4 md:grid-cols-2">
          {sections.map((section) => (
            <Card key={section.title} className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3 text-lg">
                  <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <section.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {section.content}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  )
}
